/**
 * NotificationsBell — sino com dropdown de notificações + unread count.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Briefcase, CheckCheck, MessageCircle, Radio, Receipt, Star } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { notificationsApi, type NotificationItem, type NotificationKind } from "@/lib/api-system";
import { formatRelative } from "@/lib/format";
import { cn } from "@/lib/utils";

const ICON_BY_KIND: Record<NotificationKind, typeof Briefcase> = {
  "job.created": Briefcase,
  "job.assigned": Briefcase,
  "job.completed": Briefcase,
  "job.approved": Briefcase,
  "invoice.created": Receipt,
  "invoice.approved": Receipt,
  "invoice.paid": Receipt,
  "broadcast.dispatched": Radio,
  "comment.added": MessageCircle,
  "review.received": Star,
  system: Bell,
};

export function NotificationsBell() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const count = useQuery({
    queryKey: ["notif-count"],
    queryFn: notificationsApi.unreadCount,
    refetchInterval: 30_000,
  });

  const list = useQuery({
    queryKey: ["notif-list"],
    queryFn: () => notificationsApi.list({ size: 15 }),
    enabled: open,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notif-count"] });
      qc.invalidateQueries({ queryKey: ["notif-list"] });
    },
  });

  const markAll = useMutation({
    mutationFn: notificationsApi.markAllRead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notif-count"] });
      qc.invalidateQueries({ queryKey: ["notif-list"] });
    },
  });

  const unread = count.data?.unread ?? 0;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Notificações" className="relative">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span className="absolute top-1 right-1 flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold ring-2 ring-background">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200/60 dark:border-white/10">
          <div>
            <h3 className="font-semibold text-sm">Notificações</h3>
            {unread > 0 && (
              <p className="text-[11px] text-muted-foreground">{unread} não lidas</p>
            )}
          </div>
          {unread > 0 && (
            <Button variant="ghost" size="sm" onClick={() => markAll.mutate()}>
              <CheckCheck className="h-3.5 w-3.5" /> Marcar todas
            </Button>
          )}
        </div>
        <div className="max-h-[400px] overflow-y-auto">
          {list.isLoading ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">Carregando…</div>
          ) : list.data?.items.length === 0 ? (
            <div className="px-4 py-12 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 dark:bg-white/5">
                <Bell className="h-5 w-5 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium">Tudo em dia</p>
              <p className="text-xs text-muted-foreground">Sem notificações novas.</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-200/40 dark:divide-white/5">
              {list.data?.items.map((n) => (
                <NotificationRow key={n.id} n={n} onClick={() => {
                  if (!n.read_at) markRead.mutate(n.id);
                  if (n.link) setOpen(false);
                }} />
              ))}
            </ul>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function NotificationRow({ n, onClick }: { n: NotificationItem; onClick: () => void }) {
  const Icon = ICON_BY_KIND[n.kind] ?? Bell;
  const content = (
    <div
      className={cn(
        "flex items-start gap-3 px-4 py-3 hover:bg-slate-50/60 dark:hover:bg-white/[0.03] transition-colors cursor-pointer",
        !n.read_at && "bg-primary-50/30 dark:bg-primary-500/[0.04]",
      )}
      onClick={onClick}
    >
      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 dark:bg-white/5 shrink-0">
        <Icon className="h-4 w-4 text-slate-700 dark:text-slate-300" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium truncate">{n.title}</p>
          {!n.read_at && <span className="h-1.5 w-1.5 rounded-full bg-primary-500 shrink-0" />}
        </div>
        {n.body && <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{n.body}</p>}
        <div className="flex items-center gap-2 mt-1">
          <Badge variant="muted" size="sm" className="text-[10px]">{n.kind}</Badge>
          <span className="text-[10px] text-muted-foreground">{formatRelative(n.created_at)}</span>
        </div>
      </div>
    </div>
  );
  return n.link ? <Link to={n.link}>{content}</Link> : content;
}
